import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Upload, FileText, CheckCircle2, X, Loader2 } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import { API_BASE_URL } from "../../lib/api";
import { Progress } from "../ui/progress"; // Add this import if you have a Progress component

const UploadSection = ({ onUploadComplete }) => {
  const [transcriptFile, setTranscriptFile] = useState(null);
  const [context, setContext] = useState("");
  const [instructions, setInstructions] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const { toast } = useToast();

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setTranscriptFile(file);
    }
  };

  const handleRemoveFile = () => {
    setTranscriptFile(null);
  };

  // Poll for job status
  const pollJobStatus = async (jobId) => {
    const maxAttempts = 600; // Poll for up to 30 minutes (600 * 3 seconds)
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/valueassessment/status/${jobId}`, {
          method: "GET",
        });

        if (!response.ok) {
          throw new Error("Failed to check job status");
        }

        const statusData = await response.json();

        // Update progress and message
        setProgress(statusData.progress || 0);
        setStatusMessage(statusData.message || "Processing...");

        // Check if job is complete
        if (statusData.status === "completed") {
          return statusData.result;
        } else if (statusData.status === "failed") {
          throw new Error(statusData.error || "Job failed");
        }

        // Continue polling if still processing
        attempts++;
        if (attempts >= maxAttempts) {
          throw new Error("Job processing timeout. Please try again.");
        }

        // Wait 3 seconds before next poll
        await new Promise((resolve) => setTimeout(resolve, 3000));
        return await poll();
      } catch (error) {
        throw error;
      }
    };

    return await poll();
  };

  const handleProcess = async () => {
    if (!transcriptFile) {
      toast({
        title: "Missing Information",
        description: "Please upload the interview transcript file.",
        variant: "destructive",
      });
      return;
    }

    if (!transcriptFile.name.toLowerCase().endsWith(".docx")) {
      toast({
        title: "Unsupported File",
        description: "Please upload a .docx transcription file.",
        variant: "destructive",
      });
      return;
    }

    if (!instructions.trim()) {
      toast({
        title: "Missing Instructions",
        description: "Please provide input prompt for the value assessment.",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    setProgress(0);
    setStatusMessage("Starting assessment...");

    try {
      // Step 1: Start the job
      const formData = new FormData();
      formData.append("docx_file", transcriptFile);
      formData.append("user_prompt", instructions.trim());
      if (context.trim()) {
        formData.append("client_context", context.trim());
      }

      const startResponse = await fetch(`${API_BASE_URL}/valueassessment`, {
        method: "POST",
        body: formData,
      });

      const startPayload = await startResponse.json().catch(() => null);

      if (!startResponse.ok || !startPayload || !startPayload.job_id) {
        throw new Error(
          startPayload?.error ?? "Unable to start the assessment. Please try again."
        );
      }

      const jobId = startPayload.job_id;

      toast({
        title: "Processing Started",
        description: `Job ID: ${jobId}. This may take up to 20 minutes.`,
      });

      // Step 2: Poll for completion
      const result = await pollJobStatus(jobId);

      // Step 3: Handle completion
      if (!result || !result.dashboard_data) {
        throw new Error("Assessment completed but dashboard data is missing.");
      }

      toast({
        title: "Assessment Ready",
        description: "Dashboard data generated successfully.",
      });

      onUploadComplete(result);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Unable to contact the assessment service.";

      toast({
        title: "Processing Failed",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
      setProgress(0);
      setStatusMessage("");
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Upload className="h-6 w-6 text-primary" />
            </div>
            <div>
              <CardTitle>Upload Assessment Data</CardTitle>
              <CardDescription>
                Provide the interview transcript and additional context to begin the AI
                value assessment
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="transcript">Interview Transcript File (DOCX) *</Label>
            <div className="flex flex-col gap-3">
              {!transcriptFile ? (
                <div className="relative">
                  <Input
                    id="transcript"
                    type="file"
                    accept=".docx"
                    onChange={handleFileChange}
                    className="cursor-pointer"
                    disabled={isProcessing}
                  />
                </div>
              ) : (
                <div className="flex items-center gap-2 p-3 border rounded-lg bg-accent/5">
                  <FileText className="h-5 w-5 text-primary" />
                  <span className="flex-1 text-sm text-foreground">
                    {transcriptFile.name}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRemoveFile}
                    className="h-8 w-8 p-0"
                    disabled={isProcessing}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              Upload the DOCX transcript. The AI will automatically extract answers to the
              assessment questionnaire.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="context">Additional Client Context</Label>
            <Textarea
              id="context"
              placeholder="Add any additional context about the client, industry, or specific considerations..."
              className="min-h-[120px]"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              disabled={isProcessing}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="instructions">
              Input Prompt for Value Assessment *
            </Label>
            <Textarea
              id="instructions"
              placeholder="Any specific instructions or focus areas for the assessment..."
              className="min-h-[120px]"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              disabled={isProcessing}
            />
          </div>

          {/* Progress indicator */}
          {isProcessing && (
            <div className="space-y-3 p-4 border rounded-lg bg-muted/50">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">Processing Assessment</span>
                <span className="text-muted-foreground">{progress}%</span>
              </div>
              <Progress value={progress} className="h-2" />
              <p className="text-sm text-muted-foreground">{statusMessage}</p>
            </div>
          )}

          <Button
            onClick={handleProcess}
            className="w-full va-button-primary-blue"
            size="lg"
            variant="secondary"
            disabled={isProcessing}
          >
            {isProcessing ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Processing Assessment... ({progress}%)
              </>
            ) : (
              <>
                <CheckCircle2 className="mr-2 h-5 w-5" />
                Process &amp; Generate Assessment
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-muted/50">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4" />
            What Happens Next?
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            • AI will analyze the transcript and extract responses to the assessment
            questionnaire
          </p>
          <p>
            • Calculate automation potential, efficiency, and effectiveness values for each
            process
          </p>
          <p>
            • Generate comprehensive value projections including ROSMA and decision
            optimization
          </p>
          <p>• Assess maturity and readiness across all relevant dimensions</p>
          <p>• You can review and edit any calculated values or assumptions</p>
          <p className="text-amber-600 dark:text-amber-500 font-medium">
            ⏱ This process typically takes 15-20 minutes. Please don't close this page.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default UploadSection;